import { useCallback } from 'react';
import { useMutation } from '@common-fe/common-fe';

import { verifyUserRole } from '@/modules/user/components/SignIn/hooks/verifyUserRole';
import { useAuthStore } from '@/modules/user/stores';
import Token from '@/utils/handlers/Token';

import useSignInQuery from '../queries/useSignIn.query';

export interface LoginPayload {
  username: string;
  password: string;
  newPassword?: string;
}

const useSignIn = () => {
  const { handleSignIn } = useSignInQuery();
  const { setAuth } = useAuthStore();

  const { mutateAsync, isError } = useMutation(
    async (loginPayload: LoginPayload) => {
      const authResponse = await handleSignIn(loginPayload);
      const accessToken = authResponse?.accessToken;

      if (!accessToken) {
        return authResponse;
      }

      const isLoginBlocked = verifyUserRole(accessToken);
      if (isLoginBlocked) {
        throw new Error('');
      }

      return authResponse;
    },
    {
      onSuccess: (token) => {
        const accessToken = token?.accessToken;
        const refreshToken = token?.refreshToken;
        const expiresIn = token?.expiresIn;
        const userName = token?.username;

        if (userName && accessToken && refreshToken && expiresIn) {
          Token.storeToken(accessToken);
          Token.storeRefreshToken(refreshToken);
          Token.storeExpiresIn(expiresIn);
          Token.storeUserName(userName);
          setAuth(true);
        }
      },
    }
  );

  const onChangePassword = useCallback(async (loginPayload: LoginPayload) => {
    return await mutateAsync(loginPayload);
  }, [mutateAsync]);

  const handleSetToken = (token: string) => {
    Token.storeToken(token);
    setAuth(true);
  };

  return {
    isError: isError,
    onLogin: mutateAsync,
    onChangePassword,
    handleSetToken,
  };
};

export default useSignIn;
